import { Button, Icons, Input } from '@healthinal/ui';
import { useState } from 'react';

import type { MedicationDto } from '../../../../../api/generated/model';
import { CreateMedicationDialog } from '../../../../patients/$patientId/components/CreateMedicationDialog';
import { MasterdataTable } from '../MasterTable';
import { MedicationHistoryModal } from './MedicationHistoryModal';
import { MedicationTableRow } from './MedicationTableRow';
import { useMedicationTable } from './useMedicationTable';

function MedicationTableHeader() {
  return (
    <>
      <th>Name</th>
      <th style={{ width: 65 }}>Aktiv</th>
      <th style={{ width: 100 }}>Verlauf</th>
    </>
  );
}

type MedicationDialogState = {
  open: boolean;
  medication: MedicationDto | null;
};

type HistoryModalState = {
  open: boolean;
  medication: MedicationDto | null;
};

export function MedicationTable() {
  const {
    medications,
    allMedications,
    isLoading,
    page,
    totalPages,
    search,
    setSearch,
    setPage,
    handleToggleActive,
    handleCreate,
    handleUpdate,
    isTogglePending,
    isCreatePending,
    isUpdatePending,
  } = useMedicationTable();
  const [dialogState, setDialogState] = useState<MedicationDialogState>({ open: false, medication: null });
  const [historyModalState, setHistoryModalState] = useState<HistoryModalState>({ open: false, medication: null });

  const closeDialog = () => setDialogState({ open: false, medication: null });

  return (
    <>
      <MasterdataTable
        title="Medikation"
        headerActions={
          <>
            <Input
              placeholder="Suche..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{
                width: 260,
                maxWidth: '100%',
                flexShrink: 0,
              }}
            />
            <Button
              color="primary"
              endDecorator={<Icons.Add />}
              onClick={() => setDialogState({ open: true, medication: null })}
              loading={isCreatePending}>
              Medikament erstellen
            </Button>
          </>
        }
        isLoading={isLoading}
        isEmpty={medications.length === 0}
        emptyText="Keine Daten vorhanden."
        colSpan={3}
        page={page}
        totalPages={totalPages}
        onPrevPage={() => setPage((prev) => Math.max(0, prev - 1))}
        onNextPage={() => setPage((prev) => Math.min(Math.max(0, totalPages - 1), prev + 1))}
        disablePrev={page === 0 || isLoading}
        disableNext={page + 1 >= totalPages || isLoading}
        header={<MedicationTableHeader />}>
        {medications.map((medication) => (
          <tr
            key={medication.id ?? medication.name}
            onClick={() => setDialogState({ open: true, medication })}
            style={{ textAlign: 'left', cursor: 'pointer' }}>
            <MedicationTableRow
              medication={medication}
              onToggleActive={handleToggleActive}
              onShowHistory={(selectedMedication) =>
                setHistoryModalState({ open: true, medication: selectedMedication })
              }
              isPending={isTogglePending}
            />
          </tr>
        ))}
      </MasterdataTable>

      <CreateMedicationDialog
        open={dialogState.open}
        medication={dialogState.medication}
        isLoading={isCreatePending || isUpdatePending}
        onClose={(result) => {
          closeDialog();
          if (!result) return;
          if (result.id) {
            handleUpdate(result.id, result.name);
          } else {
            handleCreate(result.name);
          }
        }}
        currentMedications={allMedications}
      />

      <MedicationHistoryModal
        open={historyModalState.open}
        onClose={() => setHistoryModalState({ open: false, medication: null })}
        medication={historyModalState.medication}
      />
    </>
  );
}
